import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { UtilService, NotificationService, TranslationService, DialogService } from '../../shared/index';
import { Router, ActivatedRoute } from '@angular/router';
import { MembershipLevelBenefitSearchParams } from '../shared/membership-level-benefit-search-params.model';
import { MembershipLevelBenefitSearchResult } from '../shared/membership-level-benefit-search-result.model';
import { MembershipLevelBenefit } from '../shared/membership-level-benefit.model';
import { ProfileBenefit } from '../shared/profile-benefit.model';
import { BenefitService } from '../shared/benefit.service';
import { ProfileMin } from '../../profiles/shared/profile-min.model';
import { ProfileService } from '../../profiles/shared/profile.service';
import { BenefitType } from '../shared/benefit-type.model';
import { BenefitFlag } from '../shared/benefit-flag.model';
import { BenefitAddComponent } from '../shared/benefit-add/benefit-add.component';
import { FeatureService } from '../../shared/feature/feature.service';
import { FeatureNameEnum } from '../../shared/feature/feature-name.enum';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'wl-profile-benefit-mgt',
  templateUrl: 'profile-benefit-mgt.component.html',
  providers: [BenefitService, ProfileService],
    standalone: false
})
export class ProfileBenefitMgtComponent implements OnInit {
  @Input() public windowed: boolean = false;

  @ViewChild('addBenefitComponent', { static: false }) addBenefitComponent: BenefitAddComponent;

  public _utilService: UtilService;
  public _notifyService: NotificationService;
  public _translationService: TranslationService;
  public _benefitService: BenefitService;
  public _route: ActivatedRoute;
  public _profileService: ProfileService;
  public _dialogService: DialogService;

  public isSearching: boolean;
  public benefitsActive: Array<ProfileBenefit>;
  public benefitsExpired: Array<ProfileBenefit>;

  public profileId: number;
  public profile: ProfileMin;

  public selectedBenefit: ProfileBenefit;
  public isSaving: boolean;
  public isRenewing: boolean;
  public benefitFlag: BenefitFlag;
  public existingBenefitTypes: Array<number>;
  public showBenefitHelp: boolean;
  public hasEventBenefits: boolean;

  public showEditBenefitModal = false;
  public showFlagBenefitModal = false;

  public constructor(
    utilService: UtilService,
    notifyService: NotificationService,
    translationService: TranslationService,
    benefitService: BenefitService,
    route: ActivatedRoute,
    profileService: ProfileService,
    dialogService: DialogService,
    public _featureService: FeatureService,
    private _title: Title
  ) {
    this._utilService = utilService;
    this._notifyService = notifyService;
    this._translationService = translationService;
    this._benefitService = benefitService;
    this._route = route;
    this._profileService = profileService;
    this._dialogService = dialogService;

    this.benefitsActive = new Array<ProfileBenefit>();
    this.benefitsExpired = new Array<ProfileBenefit>();
    this.profileId = 0;
    this.profile = new ProfileMin();
    this.selectedBenefit = new ProfileBenefit();
    this.isSaving = false;
    this.isSearching = false;
    this.isRenewing = false;
    this.showBenefitHelp = false;
    this.hasEventBenefits = true;

    this.benefitFlag = new BenefitFlag();
    this.existingBenefitTypes = new Array<number>();
  }

  ngOnInit() {
    this._route.params.subscribe((data: any) => {
      if (this._utilService.isNotNullOrUndefined(data['id'])) {
        if (this._utilService.isNumeric(data['id'])) {
          this.profileId = Number(data['id']);
          this.loadData();
        }
      }
    });

    this._featureService.has(FeatureNameEnum.BenefitManagement).then((data: boolean) => {
      this.hasEventBenefits = data;
    });
    this._benefitService.getBenefitTypes().then((data) => {
      this.showBenefitHelp = !data.length;
    });
  }

  public loadData = () => {
    this._profileService.getProfileMin(this.profileId).then((data: ProfileMin) => {
      this.profile = data;
      this._title.setTitle(data.ReportName + ' ' + this._title.getTitle());
    });
    this.getBenefits();
  };

  public getBenefits = (): void => {
    this.isSearching = true;

    this._benefitService.getProfileBenefits(this.profileId).then(
      (data: Array<ProfileBenefit>) => {
        this.benefitsActive = data.filter((x) => x.Active);
        this.benefitsExpired = data.filter((x) => !x.Active);
        this.existingBenefitTypes = this.benefitsActive.map((x) => x.BenefitTypeId);
        let currentDate = new Date();

        this.isSearching = false;
      },
      () => {
        this.isSearching = false;
      }
    );
  };

  public editBenefit = (benefit: ProfileBenefit): void => {
    this.selectedBenefit = benefit;
    this.addBenefitComponent.benefit = benefit;
    this.addBenefitComponent.setExistingBenefitTypes(this.existingBenefitTypes);
    this.showEditBenefitModal = true;
  };

  public openFlagBenefit = (benefit: ProfileBenefit): void => {
    this.selectedBenefit = benefit;
    this.benefitFlag.BenefitTypeId = benefit.BenefitTypeId;
    this.benefitFlag.ProfileId = benefit.ProfileId;
    this.benefitFlag.ProfileBenefitId = benefit.Id;
    this.benefitFlag.QuantityUsed = 0;
    this.benefitFlag.AmountUsed = 0;
    this.showFlagBenefitModal = true;
  };

  public flagBenefit = () => {
    this.benefitFlag.ProfileIds = [this.profileId];
    this._benefitService.flagProfileBenefit(this.benefitFlag).then((data) => {
      this._notifyService.success(this._translationService.getInstantTitlecase('common.saved'));
    });
    this.showFlagBenefitModal = false;
  };

  public addBenefit = (): void => {
    this.selectedBenefit = new ProfileBenefit();
    this.selectedBenefit.ProfileId = this.profileId;
    this.selectedBenefit.Active = true;
    this.selectedBenefit.ExpirationDate = this._utilService.addDays(new Date(), 365);
    this.addBenefitComponent.benefit = this.selectedBenefit;
    this.addBenefitComponent.setExistingBenefitTypes(this.existingBenefitTypes);
    this.showEditBenefitModal = true;
  };

  public saveBenefit = (): void => {
    this.isSaving = true;
    if (this.selectedBenefit.Id > 0) {
      this._benefitService.updateProfileBenefit(this.selectedBenefit).then(
        () => {
          this.getBenefits();
          this.isSaving = false;
          this.showEditBenefitModal = false;
        },
        () => {
          this.isSaving = false;
          this.showEditBenefitModal = false;
        }
      );
    } else {
      this._benefitService.createProfileBenefit(this.selectedBenefit).then(
        () => {
          this.getBenefits();
          this.isSaving = false;
          this.showEditBenefitModal = false;
        },
        () => {
          this.isSaving = false;
          this.showEditBenefitModal = false;
        }
      );
    }
  };

  public deleteBenefit = (benefit: ProfileBenefit): void => {
    this._dialogService.confirmDeleteStandardMessage(() => {
      this._benefitService.deleteProfileBenefit(benefit.Id).then(() => {
        this.getBenefits();
      });
    });
  };

  public renewAllBenefits = (): void => {
    this._notifyService.success(this._translationService.getInstant('common.saved'));
  };

  public onBenefitTypeChange = (data: string): void => {
    //the option comes thru as a string convert to int
    this.selectedBenefit.BenefitTypeId = parseInt(data);
  };

  public handleQuantityUsedChanged = (): void => {
    this.selectedBenefit.ValueUsed = this.selectedBenefit.QuantityUsed * this.selectedBenefit.Value;
  };

  public handleRenewProfileBenefitsClick = (): void => {
    this.isRenewing = true;
    let curDate = new Date();
    let hasFutureUnexpiredBenefit = false;
    for (let benefit of this.benefitsActive) {
      if (benefit.ExpirationDate > curDate) {
        hasFutureUnexpiredBenefit = true;
        break;
      }
    }
    let confirmMessage = this._translationService.getInstant('benefits.profile-benefit-mgt.renew-all-benefits-confirmation');

    if (hasFutureUnexpiredBenefit == true) {
      confirmMessage =
        confirmMessage +
        '  ' +
        this._translationService.getInstant('benefits.profile-benefit-mgt.renew-all-benefits-has-future-unexpired-warning');
    }

    this._dialogService.confirmYesNo(
      confirmMessage,
      () => {
        this._benefitService.renewProfileBenefits(this.profileId).then(
          () => {
            this.getBenefits();
            this.isRenewing = false;
          },
          () => {
            this.isRenewing = false;
          }
        );
      },
      () => {
        this.isRenewing = false;
      }
    );
  };

  public setProfile = (profileId: number) => {
    this.profileId = profileId;
    this.loadData();
  };
}
